import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { RootState } from '../../../../store/root-reducer';
import toaster, { Theme } from '../../../../shared/toaster';
import {
  LeadFinderPrimaryTab,
  LeadFinderSecondaryTab,
  LeadFinderListTab,
  LeadFinderListVisibility,
  LeadFinderList,
  LeadRevealType,
  SavedTabFilters,
} from '../../types';
import {
  createListAPI,
  updateListAPI,
  deleteListAPI,
  exportAllCompaniesAPI,
  exportAllLeadsAPI,
  getSavedLeads,
  getSavedCompanies,
} from '../../helpers/leads.api';
import { setShowGlobalRestrictionModal } from '../../v2-lead-slice';
// import {
//   getUserTrackingId,
//   getUserShAccountId,
//   getUserPlanName,
//   getUserRole,
//   getUserLeadFinderPlanName,
//   leadFinderBulkOperationLimit,
// } from '../../../../shared/utils/user-details';
// import { AnalyticsEvents } from '../../../../shared/enums/analytics';
import {
  decodeCreatedDateToRange,
  formatCreatedDateRangeForApi,
} from '../../helpers/helper';
import {
  // buildSavedSearchFilters,
  normalizeSavedResponse,
  clearPeopleSelection,
  clearCompanySelection,
  checkCompanyCredits,
  ListManagementContext,
  saveListCompanyTotalNew,
  saveListPeopleNonSaved,
  saveListPeopleSaved,
  saveListCompanySaved,
  addToListCompanyTotalNew,
  addToListSavedBulk,
  addToListPeopleBulk,
  addToListPeopleSingle,
  addToListActionFallback,
  removeFromListSavedBulk,
  removeFromListTotalNewBulk,
  removeFromListSingle,
} from './listManagementUtils';
import { openCreditNotAvailableModal } from '../../v2-lead-slice';
import {
  leadFinderBulkOperationLimit,
  getUserTrackingId,
  getUserShAccountId,
  getUserPlanName,
  getUserRole,
  getUserLeadFinderPlanName,
} from '../../../../shared/utils/user-details';
import { AnalyticsEvents } from '../../../../shared/enums/analytics';

interface UseListManagementProps {
  activeLeadType: LeadFinderPrimaryTab;
  activeSubTab: LeadFinderSecondaryTab;
  leadsData: any;
  setLeadsData: (data: any) => void;
  apiParam: any;
  savedApiParam: any;
  handleGeneratePayload: (filters?: any) => any;
  searchLeads: (payload?: any) => void;
  refreshPeopleLists: () => void;
  refreshCompanyListsForPeopleTab: () => void;
  companiesData: any;
  setCompaniesData: (data: any) => void;
  companySavedApiParam: any;
  handleCompanyGeneratePayload: (filters?: any) => any;
  searchCompanies: (payload?: any) => void;
  refreshCompanyLists: () => void;
  savedFilters: SavedTabFilters;
  setSavedFilters: React.Dispatch<React.SetStateAction<SavedTabFilters>>;
  companyIdToAddFromRowRef: React.MutableRefObject<number | null>;
  leadIdToAddFromRowRef: React.MutableRefObject<number | null>;
  refreshLeadsAndStartPollingRef: React.MutableRefObject<() => void>;
  maybeEnableNotificationPollingRef: React.MutableRefObject<(res: any) => void>;
  addFirebaseNotificationRef: React.MutableRefObject<
    (notificationUuId: string, revealType: any, totalLeads: number) => void
  >;
  leadCredits: number;
  isDemoAccount: boolean;
  isCompanyTab: boolean;
  handleFetchSavedCounts?: (payload: any) => Promise<number | void>;
  handleCompanyTriggerSearchWithFilters?: (filters: URLSearchParams) => void;
  handleBulkRevealResponse?: (res: any, companyIds: number[]) => void;
  setEnrichingCompanies: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  companyApiParam?: any;
}

export const useListManagement = (props: UseListManagementProps) => {
  const {
    activeLeadType,
    activeSubTab,
    leadsData,
    setLeadsData,
    apiParam,
    savedApiParam,
    handleGeneratePayload,
    searchLeads,
    refreshPeopleLists,
    refreshCompanyListsForPeopleTab,
    companiesData,
    setCompaniesData,
    companySavedApiParam,
    handleCompanyGeneratePayload,
    searchCompanies,
    refreshCompanyLists,
    savedFilters,
    setSavedFilters,
    companyIdToAddFromRowRef,
    leadIdToAddFromRowRef,
    refreshLeadsAndStartPollingRef,
    maybeEnableNotificationPollingRef,
    addFirebaseNotificationRef,
    leadCredits,
    isDemoAccount,
    isCompanyTab,
    handleBulkRevealResponse,
    setEnrichingCompanies,
    companyApiParam,
    handleFetchSavedCounts,
    handleCompanyTriggerSearchWithFilters,
  } = props;

  const dispatch = useDispatch();
  const historyHook = useHistory();
  const locationHook = useLocation();

  const {
    selectedLeads,
    isSelectedAllLeads,
    selectedCompanies,
    isSelectedAllCompanies,
    peopleMaxLeadsLimit,
    companyMaxLeadsLimit,
  } = useSelector((state: RootState) => state.leadFinderV2.selection);

  const [isCreateListLoading, setIsCreateListLoading] = useState(false);
  const [isDeleteListLoading, setIsDeleteListLoading] = useState(false);
  const [loadingRemoveFromList, setLoadingRemoveFromList] = useState(false);

  const checkPeopleCredits = useCallback(
    (revealType: LeadRevealType) => {
      // Only applies to People tab actions
      if (isCompanyTab) return true;

      // Calculate count (same strategy as useBulkReveal)
      const leadFinderBulkOperationLimitValue = leadFinderBulkOperationLimit();
      let count = 0;
      if (isSelectedAllLeads) {
        if (peopleMaxLeadsLimit != null) {
          count = peopleMaxLeadsLimit;
        } else {
          count =
            leadsData?.pagination?.totalRows > leadFinderBulkOperationLimitValue
              ? leadFinderBulkOperationLimitValue
              : leadsData?.pagination?.totalRows || 0;
        }
      } else {
        // For manual selection, credit validation should be based on the
        // number of selected leads (not just "unrevealed" ones), otherwise
        // users can select 25 leads with 10 credits and still proceed.
        count = selectedLeads.length;
      }

      const creditCostPerLead: Record<string, number> = {
        [LeadRevealType.Email]: 1,
        [LeadRevealType.Phone]: 6,
        [LeadRevealType.PersonalEmail]: 2,
        [LeadRevealType.EmailPhone]: 7,
        [LeadRevealType.PersonalEmailPhone]: 8,
        [LeadRevealType.EmailPersonalEmail]: 3,
        [LeadRevealType.EmailPersonalEmailPhone]: 9,
      };
      const costPerLead = creditCostPerLead[revealType] || 1;

      if (Number(leadCredits) < count * costPerLead) {
        dispatch(openCreditNotAvailableModal(revealType));
        window.analytics?.track?.({
          userId: getUserTrackingId(),
          event: AnalyticsEvents.TrippedFeature,
          properties: {
            shAccountId: getUserShAccountId(),
            current_plan: getUserPlanName(),
            role: getUserRole(),
            'LF Plan': getUserLeadFinderPlanName(),
            feature_type: 'Lead Finder',
          },
        });
        return false;
      }
      return true;
    },
    [
      isCompanyTab,
      isSelectedAllLeads,
      peopleMaxLeadsLimit,
      leadsData,
      selectedLeads,
      leadCredits,
      dispatch,
    ],
  );

  const buildCreatedDateRangeParam = useCallback(
    (startDate: Date, endDate: Date, shouldEncode = false) =>
      formatCreatedDateRangeForApi(startDate, endDate, {
        encode: shouldEncode,
      }),
    [],
  );

  // Internal ref for saved search filters (was module-level variable)
  const savedSearchFiltersToUseRef = useRef<any>(null);

  // Normalize listIds for API payloads: return { includes, excludes } or undefined.
  const normalizeListIdsForPayload = (listIds: any) => {
    if (!listIds) return undefined;
    // If already an array, treat it as includes
    if (Array.isArray(listIds)) {
      return { includes: listIds };
    }
    // If object, prefer includes/excludes if present
    if (typeof listIds === 'object') {
      const hasIncludes =
        Array.isArray(listIds.includes) && listIds.includes.length > 0;
      const hasExcludes =
        Array.isArray(listIds.excludes) && listIds.excludes.length > 0;
      if (hasIncludes || hasExcludes) {
        return {
          ...(hasIncludes ? { includes: listIds.includes } : {}),
          ...(hasExcludes ? { excludes: listIds.excludes } : {}),
        };
      }
      // If includes exists but empty array, still send includes (backend may expect it)
      if (Array.isArray(listIds.includes)) {
        return { includes: listIds.includes };
      }
    }
    return undefined;
  };

  const getCompanyListIdsFromUrl = useCallback(() => {
    const queryParams = new URLSearchParams(locationHook.search);
    const includesStr = queryParams.get('company_list');
    const excludesStr = queryParams.get('excludeCompany_list');
    const listIds: any = {};

    if (includesStr) {
      const includes = includesStr
        .split(',')
        .map((id) => parseInt(id.replace(/"/g, '').trim(), 10))
        .filter((id) => !Number.isNaN(id) && id > 0);
      if (includes.length > 0) listIds.includes = includes;
    }

    if (excludesStr) {
      const excludes = excludesStr
        .split(',')
        .map((id) => parseInt(id.replace(/"/g, '').trim(), 10))
        .filter((id) => !Number.isNaN(id) && id > 0);
      if (excludes.length > 0) listIds.excludes = excludes;
    }

    return Object.keys(listIds).length > 0 ? listIds : undefined;
  }, [locationHook.search]);

  // Helpers to prefer row action ids when action opened from a row
  const getEffectiveSelectedLeads = () =>
    leadIdToAddFromRowRef.current != null
      ? [leadIdToAddFromRowRef.current]
      : selectedLeads;

  const getEffectiveSelectedCompanies = () =>
    companyIdToAddFromRowRef.current != null
      ? [companyIdToAddFromRowRef.current]
      : selectedCompanies;

  const handleCreateListOnly = useCallback(
    async (
      listName: string,
      isShared: boolean,
      teamIds: number[] = [],
    ): Promise<void> => {
      try {
        setIsCreateListLoading(true);

        const activeTab =
          activeLeadType === LeadFinderPrimaryTab.COMPANY
            ? LeadFinderListTab.Company
            : LeadFinderListTab.People;

        // Determine visibility
        let visibility: LeadFinderListVisibility;
        if (isShared) {
          visibility = LeadFinderListVisibility.Public;
        } else if (teamIds.length > 0) {
          visibility = LeadFinderListVisibility.Team;
        } else {
          visibility = LeadFinderListVisibility.Private;
        }

        const payload: any = {
          name: listName,
          visibility,
          tab: activeTab,
        };

        if (teamIds.length > 0) {
          payload.teamIds = teamIds;
        }

        const createResponse = await createListAPI(payload);
        toaster.success(
          createResponse?.message ||
            createResponse?.successMessage ||
            'List created successfully',
          {
            theme: Theme.New,
          },
        );
        try {
          await Promise.all([
            // Refresh lists stored by the people-finder hook (used on People tab)
            refreshCompanyListsForPeopleTab(),
            // Refresh lists stored by the company-finder hook (used on Company tab)
            refreshCompanyLists(),
            // Refresh people lists
            refreshPeopleLists(),
          ]);
        } catch (_err) {
          // Ignore refresh errors; list was created successfully.
        }
        return;
      } catch (e: any) {
        if (!e?.isHandled) {
          toaster.error(
            e?.message || "Couldn't create list. Please try again.",
          );
        }
        throw e;
      } finally {
        setIsCreateListLoading(false);
      }
    },
    [
      activeLeadType,
      refreshCompanyListsForPeopleTab,
      refreshCompanyLists,
      refreshPeopleLists,
    ],
  );

  const refreshListsAndSavedData = useCallback(
    async (deletedListId?: number) => {
      const isPeopleTab = activeLeadType === LeadFinderPrimaryTab.PEOPLE;
      try {
        refreshPeopleLists();
      } catch (e) {
        // ignore
      }
      try {
        refreshCompanyListsForPeopleTab();
      } catch (e) {
        // ignore
      }
      try {
        // Refresh company lists managed by company-finder hook (company sidebar)
        refreshCompanyLists();
      } catch (e) {
        // ignore
      }

      if (activeSubTab === LeadFinderSecondaryTab.SAVED) {
        const currentSavedApiParam = isPeopleTab
          ? savedApiParam
          : companySavedApiParam;
        const payloadForSaved: any = {
          start: currentSavedApiParam?.start || 1,
          take: currentSavedApiParam?.take || 25,
        };
        if (savedFilters.search) {
          payloadForSaved.search = savedFilters.search;
        }
        if (savedFilters.tags && savedFilters.tags.length > 0) {
          payloadForSaved.tagIds = savedFilters.tags;
        }
        if (savedFilters.createdDate && savedFilters.createdDate.length === 2) {
          const [startDate, endDate] = savedFilters.createdDate;
          payloadForSaved.createdDate = buildCreatedDateRangeParam(
            startDate,
            endDate,
            true,
          );
        }
        if (savedFilters.listIds && savedFilters.listIds.includes?.length > 0) {
          let listIds = savedFilters.listIds.includes;
          if (deletedListId !== undefined) {
            listIds = listIds.filter((id) => id !== deletedListId);
          }
          if (listIds.length > 0) {
            payloadForSaved.listIds = listIds;
          }
        }
        if (savedFilters.excludeProspects) {
          payloadForSaved.excludeProspects = savedFilters.excludeProspects;
        }
        if (savedFilters.emailStatus && savedFilters.emailStatus.length > 0) {
          payloadForSaved.emailStatus = savedFilters.emailStatus;
        }

        if (isPeopleTab) {
          const res = await getSavedLeads(payloadForSaved);
          if (res?.payload) {
            const savedPayload = res?.payload || {};
            const savedProfiles = Array.isArray(savedPayload)
              ? savedPayload
              : savedPayload?.profiles || [];
            const savedPaginationSource = Array.isArray(savedPayload)
              ? null
              : savedPayload?.pagination;

            const takeValue =
              savedPaginationSource?.take || payloadForSaved?.take || 25;
            const startValue =
              savedPaginationSource?.start || payloadForSaved?.start || 1;
            const pageNumber = Math.ceil(startValue / takeValue);

            const savedPagination = savedPaginationSource
              ? {
                  page: pageNumber,
                  limit: takeValue,
                  totalRows: savedPaginationSource.total || 0,
                }
              : {
                  page: pageNumber,
                  limit: takeValue,
                  totalRows: savedProfiles.length,
                };

            setLeadsData({
              profiles: [...savedProfiles],
              pagination: savedPagination,
            });
          }
        } else {
          const res = isPeopleTab
            ? await getSavedLeads(payloadForSaved)
            : await getSavedCompanies(payloadForSaved);
          if (res?.payload) {
            const savedPayload = res?.payload || {};
            const savedProfiles = Array.isArray(savedPayload)
              ? savedPayload
              : savedPayload?.companies || [];
            const savedPaginationSource = Array.isArray(savedPayload)
              ? null
              : savedPayload?.pagination;

            const takeValue =
              savedPaginationSource?.take || payloadForSaved?.take || 25;
            const startValue =
              savedPaginationSource?.start || payloadForSaved?.start || 1;
            const pageNumber = Math.ceil(startValue / takeValue);

            const savedPagination = savedPaginationSource
              ? {
                  page: pageNumber,
                  limit: takeValue,
                  totalRows: savedPaginationSource.total || 0,
                }
              : {
                  page: pageNumber,
                  limit: takeValue,
                  totalRows: savedProfiles.length,
                };

            setCompaniesData({
              profiles: [...savedProfiles],
              pagination: savedPagination,
            });
          }
        }
      }
    },
    [
      activeLeadType,
      activeSubTab,
      buildCreatedDateRangeParam,
      refreshPeopleLists,
      refreshCompanyLists,
      savedApiParam,
      companySavedApiParam,
      savedFilters,
      setLeadsData,
      setCompaniesData,
      refreshCompanyListsForPeopleTab,
    ],
  );

  // Handle create/update list API call
  const handleSaveList = useCallback(
    async (
      listId: number | null,
      listName: string,
      isShared: boolean,
      teamIds: number[] = [],
      shouldEnrich = true,
      revealType: LeadRevealType = LeadRevealType.Email,
    ) => {
      const revealTypeToUse = revealType;

      if (isCompanyTab && shouldEnrich) {
        const rowCompanyId = companyIdToAddFromRowRef.current;
        const effectiveSelectedCompanies =
          rowCompanyId != null ? [rowCompanyId] : selectedCompanies;
        const ok = checkCompanyCredits(
          rowCompanyId,
          isSelectedAllCompanies,
          companiesData,
          effectiveSelectedCompanies,
          leadCredits,
          dispatch,
        );
        if (!ok) return;
      }
      if (!isCompanyTab) {
        const ok = checkPeopleCredits(revealTypeToUse);
        if (!ok) return;
      }

      try {
        setIsCreateListLoading(true);

        const activeTab =
          activeLeadType === LeadFinderPrimaryTab.COMPANY
            ? LeadFinderListTab.Company
            : LeadFinderListTab.People;

        // Determine visibility
        let visibility: LeadFinderListVisibility;
        if (isShared) {
          visibility = LeadFinderListVisibility.Public;
        } else if (teamIds.length > 0) {
          visibility = LeadFinderListVisibility.Team;
        } else {
          visibility = LeadFinderListVisibility.Private;
        }

        const payload: any = {
          name: listName,
          visibility,
          tab: activeTab,
        };

        if (teamIds.length > 0) {
          payload.teamIds = teamIds;
        }

        if (listId === null) {
          const isCompany = activeLeadType === LeadFinderPrimaryTab.COMPANY;
          const isPeople = activeLeadType === LeadFinderPrimaryTab.PEOPLE;
          const isTotalOrNetNew =
            activeSubTab === LeadFinderSecondaryTab.TOTAL ||
            activeSubTab === LeadFinderSecondaryTab.NEW;

          const branchParams = {
            listName,
            visibility,
            teamIds,
            shouldEnrich,
            revealTypeToUse,
            activeTab,
          };

          const saveRowLeadId = leadIdToAddFromRowRef.current;
          const saveRowCompanyId = companyIdToAddFromRowRef.current;
          const isFromRowAction =
            saveRowLeadId != null || saveRowCompanyId != null;

          const ctx: ListManagementContext = {
            dispatch,
            selectedLeads,
            selectedCompanies,
            isSelectedAllLeads: isFromRowAction ? false : isSelectedAllLeads,
            isSelectedAllCompanies: isFromRowAction
              ? false
              : isSelectedAllCompanies,
            peopleMaxLeadsLimit,
            companyMaxLeadsLimit,
            companiesData,
            activeSubTab,
            activeLeadType,
            isCompanyTab,
            apiParam,
            savedApiParam,
            companySavedApiParam,
            companyApiParam,
            savedFilters,
            leadCredits,
            handleGeneratePayload,
            handleCompanyGeneratePayload,
            refreshListsAndSavedData,
            refreshPeopleLists,
            refreshCompanyLists,
            setEnrichingCompanies,
            setIsCreateListLoading,
            setLeadsData,
            setCompaniesData,
            savedSearchFiltersToUseRef,
            refreshLeadsAndStartPollingRef,
            maybeEnableNotificationPollingRef,
            addFirebaseNotificationRef,
            companyIdToAddFromRowRef,
            getEffectiveSelectedLeads,
            getEffectiveSelectedCompanies,
            handleBulkRevealResponse,
            normalizeListIdsForPayload,
          };

          if (isCompany && isTotalOrNetNew) {
            await saveListCompanyTotalNew(ctx, branchParams);
          } else if (
            isPeople &&
            activeSubTab === LeadFinderSecondaryTab.TOTAL
          ) {
            await saveListPeopleNonSaved(ctx, branchParams, 'total');
            return;
          } else if (isPeople && activeSubTab === LeadFinderSecondaryTab.NEW) {
            await saveListPeopleNonSaved(
              ctx,
              branchParams,
              LeadFinderSecondaryTab.NEW,
            );
            return;
          } else if (
            isPeople &&
            activeSubTab === LeadFinderSecondaryTab.SAVED
          ) {
            await saveListPeopleSaved(ctx, branchParams);
            return;
          } else if (
            isCompany &&
            activeSubTab === LeadFinderSecondaryTab.SAVED
          ) {
            await saveListCompanySaved(ctx, branchParams);
            return;
          } else {
            // For other tabs, call createListAPI as usual
            const createResponse = await createListAPI(payload);
            toaster.success(
              createResponse?.message ||
                createResponse?.successMessage ||
                'List created successfully',
              {
                theme: Theme.New,
              },
            );
          }
        } else {
          const updateResponse = await updateListAPI(listId, payload);
          toaster.success(
            updateResponse?.message ||
              updateResponse?.successMessage ||
              'List updated successfully',
            {
              theme: Theme.New,
            },
          );
          // Refresh in the background so the Edit modal closes as soon as the
          refreshListsAndSavedData().catch(() => {
            toaster.error('Failed to update list', { theme: Theme.New });
          });
          return;
        }

        await refreshListsAndSavedData();
      } catch (e: any) {
        if (!e?.isHandled) {
          const errorMessage =
            listId === null
              ? "Couldn't create list. Please try again."
              : "Couldn't update list. Please try again.";
          toaster.error(e.message || errorMessage);
        }
        throw e;
      } finally {
        setIsCreateListLoading(false);
        leadIdToAddFromRowRef.current = null;
        companyIdToAddFromRowRef.current = null;
      }
    },
    [
      activeLeadType,
      activeSubTab,
      leadsData,
      refreshCompanyLists,
      refreshPeopleLists,
      refreshCompanyListsForPeopleTab,
      companiesData,
      selectedCompanies,
      isSelectedAllCompanies,
      handleCompanyGeneratePayload,
      selectedLeads,
      isSelectedAllLeads,
      handleGeneratePayload,
      apiParam,
      savedApiParam,
      companySavedApiParam,
      savedFilters,
      setLeadsData,
      setCompaniesData,
      refreshListsAndSavedData,
      leadCredits,
      isCompanyTab,
      checkPeopleCredits,
      dispatch,
      companyIdToAddFromRowRef,
      leadIdToAddFromRowRef,
      refreshLeadsAndStartPollingRef,
      maybeEnableNotificationPollingRef,
      addFirebaseNotificationRef,
      setEnrichingCompanies,
    ],
  );

  // Handle delete list API call
  const handleDeleteList = useCallback(
    async (listId: number) => {
      try {
        setIsDeleteListLoading(true);

        const deleteResponse = await deleteListAPI(listId);

        toaster.success(
          deleteResponse?.message ||
            deleteResponse?.successMessage ||
            'List deleted successfully',
          {
            theme: Theme.New,
          },
        );

        if (activeSubTab === LeadFinderSecondaryTab.SAVED) {
          const listIdStr = String(listId);

          // Update savedFilters - remove deleted list from includes and excludes
          setSavedFilters((prev) => {
            const newIncludes =
              prev.listIds?.includes?.filter((id) => id !== listId) ?? [];
            const newExcludes =
              prev.listIds?.excludes?.filter((id) => id !== listId) ?? [];
            return {
              ...prev,
              listIds: {
                includes: newIncludes,
                excludes: newExcludes,
              },
            };
          });

          // Update URL - remove deleted list from list params
          const queryParams = new URLSearchParams(locationHook.search);
          const listParamKeys = [
            'company_list',
            'people_list',
            'excludeCompany_list',
            'excludePeople_list',
          ];
          listParamKeys.forEach((key) => {
            const value = queryParams.get(key);
            if (value) {
              const ids = value
                .split(',')
                .map((id) => id.replace(/"/g, '').trim());
              const updatedIds = ids.filter((id) => id !== listIdStr);
              queryParams.delete(key);
              if (updatedIds.length > 0) {
                queryParams.set(key, updatedIds.join(','));
              }
            }
          });
          historyHook.replace({
            pathname: locationHook.pathname,
            search: queryParams.toString(),
          });
        }

        // Refresh sidebar lists only - useCompanyFinder's useEffect will refetch saved data
        // when location.search changes (from history.replace above), avoiding duplicate API call
        if (activeLeadType === LeadFinderPrimaryTab.PEOPLE) {
          refreshPeopleLists();
        } else {
          refreshCompanyLists();
        }
      } catch (e: any) {
        if (!e?.isHandled) {
          toaster.error(e.message || "Couldn't delete list. Please try again.");
        }
      } finally {
        setIsDeleteListLoading(false);
      }
    },
    [
      activeSubTab,
      activeLeadType,
      setSavedFilters,
      locationHook.search,
      locationHook.pathname,
      historyHook,
      refreshCompanyLists,
      refreshPeopleLists,
    ],
  );

  const handleExportList = useCallback(
    async (listId: number, listName: string) => {
      if (isDemoAccount) {
        dispatch(setShowGlobalRestrictionModal(true));
        return;
      }
      try {
        let result;
        if (activeLeadType === LeadFinderPrimaryTab.PEOPLE) {
          result = await exportAllLeadsAPI({
            deSelectedLeadIds: [],
            leadsFilter: {
              listIds: [listId],
            },
          } as any);
        } else {
          result = await exportAllCompaniesAPI({
            deSelectedCompanyIds: [],
            companiesFilter: {
              listIds: {
                includes: [listId],
              },
            },
          } as any);
        }

        const { message, status } = result?.payload || {};

        if (status === 0) {
          toaster.error(
            message ||
              (activeLeadType === LeadFinderPrimaryTab.PEOPLE
                ? 'Failed to export leads'
                : 'Failed to export companies'),
            {
              theme: Theme.New,
            },
          );
        } else if (status === 2) {
          toaster.warning(
            message || 'Another export action is already in progress.',
            { theme: Theme.New },
          );
        } else if (status === 1 || status === 3 || status === undefined) {
          // backend may use different status codes; treat as success for non-zero/non-failure
          toaster.success(message || `Export for "${listName}" has started.`, {
            theme: Theme.New,
          });
        }
      } catch (e: any) {
        if (!e?.isHandled) {
          toaster.error(
            e?.message ||
              (activeLeadType === LeadFinderPrimaryTab.PEOPLE
                ? 'Error while exporting lead data'
                : 'Error while exporting company data'),
            {
              theme: Theme.New,
            },
          );
        }
      }
    },
    [activeLeadType, dispatch, isDemoAccount],
  );

  const handleAddToList = useCallback(
    async (
      listIds: number[],
      shouldEnrich = true,
      newLists?: Array<{
        name: string;
        visibility: string;
        teamIds?: number[];
      }>,
      revealType: LeadRevealType = LeadRevealType.Email,
    ) => {
      const isPeopleTab = activeLeadType === LeadFinderPrimaryTab.PEOPLE;
      const rowCompanyId = companyIdToAddFromRowRef.current;
      const rowLeadId = leadIdToAddFromRowRef.current;
      const isFromRowAction = rowLeadId != null || rowCompanyId != null;
      // When opening from row Save icon, use that company/lead instead of selection
      const effectiveSelectedCompanies =
        rowCompanyId != null ? [rowCompanyId] : selectedCompanies;
      const effectiveSelectedLeads =
        rowLeadId != null ? [rowLeadId] : selectedLeads;
      const isSelectedAll = isFromRowAction
        ? false
        : (isPeopleTab && isSelectedAllLeads) ||
          (!isPeopleTab && isSelectedAllCompanies);
      const isSelectedAllLeadsForAction =
        !isFromRowAction && isSelectedAllLeads;
      const isSelectedAllCompaniesForAction =
        !isFromRowAction && isSelectedAllCompanies;

      // ─── Credit check ──────────────────────────────────────────────────────
      if (isCompanyTab && shouldEnrich) {
        const ok = checkCompanyCredits(
          rowCompanyId,
          isSelectedAllCompanies,
          companiesData,
          effectiveSelectedCompanies,
          leadCredits,
          dispatch,
        );
        if (!ok) return false;
      }
      if (!isCompanyTab) {
        const ok = checkPeopleCredits(revealType);
        if (!ok) return false;
      }

      const ctx: ListManagementContext = {
        dispatch,
        selectedLeads,
        selectedCompanies,
        isSelectedAllLeads: isSelectedAllLeadsForAction,
        isSelectedAllCompanies: isSelectedAllCompaniesForAction,
        peopleMaxLeadsLimit,
        companyMaxLeadsLimit,
        companiesData,
        activeSubTab,
        activeLeadType,
        isCompanyTab,
        apiParam,
        savedApiParam,
        companySavedApiParam,
        companyApiParam,
        savedFilters,
        leadCredits,
        handleGeneratePayload,
        handleCompanyGeneratePayload,
        refreshListsAndSavedData,
        refreshPeopleLists,
        refreshCompanyLists,
        setEnrichingCompanies,
        setIsCreateListLoading,
        setLeadsData,
        setCompaniesData,
        savedSearchFiltersToUseRef,
        refreshLeadsAndStartPollingRef,
        maybeEnableNotificationPollingRef,
        addFirebaseNotificationRef,
        companyIdToAddFromRowRef,
        getEffectiveSelectedLeads,
        getEffectiveSelectedCompanies,
        handleBulkRevealResponse,
        newLists,
        listIds,
        normalizeListIdsForPayload,
      };

      try {
        const isSavedTab = activeSubTab === LeadFinderSecondaryTab.SAVED;
        const isTotalOrNetNew =
          activeSubTab === LeadFinderSecondaryTab.TOTAL ||
          activeSubTab === LeadFinderSecondaryTab.NEW;

        let lastApiResponse: any = null;

        // ─── Dispatch to correct branch ──────────────────────────────────────
        if (isCompanyTab && !isSavedTab) {
          lastApiResponse = await addToListCompanyTotalNew(ctx, {
            shouldEnrich,
            revealType,
            isSelectedAll,
            effectiveSelectedCompanies,
          });
        } else if (isSavedTab && isSelectedAll) {
          lastApiResponse = await addToListSavedBulk(ctx, {
            shouldEnrich,
            revealType,
            isSelectedAll,
            effectiveSelectedCompanies,
          });
        } else if (
          isPeopleTab &&
          activeSubTab === LeadFinderSecondaryTab.NEW &&
          isSelectedAll
        ) {
          lastApiResponse = await addToListPeopleBulk(
            ctx,
            LeadFinderSecondaryTab.NEW,
            { revealType },
          );
        } else if (
          isPeopleTab &&
          activeSubTab === LeadFinderSecondaryTab.NEW &&
          !isSelectedAll
        ) {
          lastApiResponse = await addToListPeopleSingle(
            ctx,
            LeadFinderSecondaryTab.NEW,
            { revealType },
          );
        } else if (
          isPeopleTab &&
          activeSubTab === LeadFinderSecondaryTab.TOTAL &&
          isSelectedAll
        ) {
          lastApiResponse = await addToListPeopleBulk(ctx, 'total', {
            revealType,
          });
        } else if (
          isPeopleTab &&
          activeSubTab === LeadFinderSecondaryTab.TOTAL &&
          !isSelectedAll
        ) {
          lastApiResponse = await addToListPeopleSingle(
            ctx,
            LeadFinderSecondaryTab.TOTAL,
            { revealType },
          );
        } else if (
          !(isCompanyTab && isTotalOrNetNew) &&
          !(isPeopleTab && activeSubTab === LeadFinderSecondaryTab.NEW)
        ) {
          lastApiResponse = await addToListActionFallback(ctx, {
            shouldEnrich,
            revealType,
            effectiveSelectedCompanies,
          });
          if (lastApiResponse === null) return false; // credit check failed
        }

        // Check if Firebase is handling this notification (people tab only)
        const firebaseNotifUuId =
          lastApiResponse?.payload?.notificationData?.notificationUuId;
        const isFirebaseHandling = isPeopleTab && !!firebaseNotifUuId;

        // If Firebase didn't set up yet (e.g. addToListPeopleBulk), do it now
        if (isFirebaseHandling) {
          addFirebaseNotificationRef.current(
            firebaseNotifUuId,
            revealType,
            lastApiResponse?.payload?.notificationData?.totalLeads || 0,
          );
        }

        if (!isFirebaseHandling) {
          maybeEnableNotificationPollingRef.current(lastApiResponse);
        }

        if (isPeopleTab) {
          toaster.success(
            lastApiResponse?.message ||
              lastApiResponse?.successMessage ||
              'Leads added to list(s) successfully',
            {
              theme: Theme.New,
            },
          );
        }
        // Clear selection after successful add
        if (isPeopleTab) {
          clearPeopleSelection(dispatch);
          refreshPeopleLists();
          refreshCompanyListsForPeopleTab();
        } else {
          clearCompanySelection(dispatch);
        }

        // Refresh data based on active subTab
        if (activeSubTab === LeadFinderSecondaryTab.SAVED) {
          const currentSavedApiParam = isPeopleTab
            ? savedApiParam
            : companySavedApiParam;
          const payloadForSaved: any = {
            start: currentSavedApiParam?.start || 1,
            take: currentSavedApiParam?.take || 25,
          };
          if (savedFilters?.search) {
            payloadForSaved.search = savedFilters.search;
          }
          if (savedFilters?.tags && savedFilters.tags.length > 0) {
            payloadForSaved.tagIds = savedFilters.tags;
          }
          if (
            savedFilters?.createdDate &&
            savedFilters.createdDate.length === 2
          ) {
            const [startDate, endDate] = savedFilters.createdDate;
            payloadForSaved.createdDate = buildCreatedDateRangeParam(
              startDate,
              endDate,
              isPeopleTab,
            );
          }
          if (savedFilters?.listIds) {
            const normalized = normalizeListIdsForPayload(savedFilters.listIds);
            if (normalized) payloadForSaved.listIds = normalized;
          }
          if (savedFilters?.excludeProspects) {
            payloadForSaved.excludeProspects = savedFilters.excludeProspects;
          }
          if (
            savedFilters?.emailStatus &&
            savedFilters.emailStatus.length > 0
          ) {
            payloadForSaved.emailStatus = savedFilters.emailStatus;
          }

          if (isPeopleTab) {
            const res = await getSavedLeads(payloadForSaved);
            const normalized = normalizeSavedResponse(
              res,
              payloadForSaved,
              'profiles',
            );
            if (normalized) setLeadsData(normalized);
            refreshPeopleLists();
          } else {
            const res = isPeopleTab
              ? await getSavedLeads(payloadForSaved)
              : await getSavedCompanies(payloadForSaved);
            const normalized = normalizeSavedResponse(
              res,
              payloadForSaved,
              'companies',
            );
            if (normalized) setCompaniesData(normalized);
            refreshCompanyLists();
          }
        }
        // For Total and Net New tabs:
        // - People tab + Firebase: no-op (Firebase listener refreshes leads on progress/complete)
        // - People tab + non-Firebase: refresh via searchLeads()
        // - Company tab + Total/NetNew: DO NOT trigger immediate searchCompanies() after
        //   reveal/add-to-list because it flips isCompanyLoading=true and shows table skeleton.
        //   Company enrichment refresh should happen via polling search only.
        // - Company tab fallback: searchCompanies()
        else if (isPeopleTab && !isFirebaseHandling) {
          refreshLeadsAndStartPollingRef.current();
        } else if (isCompanyTab && isTotalOrNetNew) {
          // no-op (polling handles search refresh)
        } else if (!isPeopleTab) {
          const urlFilters = new URLSearchParams(locationHook.search);
          const payload =
            typeof handleCompanyGeneratePayload === 'function' &&
            handleCompanyGeneratePayload(urlFilters);
          // : undefined;
          searchCompanies(payload);
        }
        // else: People tab + Firebase — Firebase listener handles lead refresh
        return true;
      } catch (error: any) {
        toaster.error(error?.message || 'Failed to add leads to list(s)');
        setEnrichingCompanies({});
        return false;
      } finally {
        leadIdToAddFromRowRef.current = null;
        companyIdToAddFromRowRef.current = null;
      }
    },
    [
      activeLeadType,
      activeSubTab,
      leadsData,
      selectedLeads,
      selectedCompanies,
      isSelectedAllLeads,
      isSelectedAllCompanies,
      handleGeneratePayload,
      handleCompanyGeneratePayload,
      savedApiParam,
      companySavedApiParam,
      apiParam,
      searchLeads,
      searchCompanies,
      setLeadsData,
      setCompaniesData,
      companiesData,
      savedFilters,
      leadCredits,
      isCompanyTab,
      dispatch,
      checkPeopleCredits,
      companyIdToAddFromRowRef,
      leadIdToAddFromRowRef,
      refreshLeadsAndStartPollingRef,
      maybeEnableNotificationPollingRef,
      refreshListsAndSavedData,
      refreshPeopleLists,
      refreshCompanyListsForPeopleTab,
      refreshCompanyLists,
      setEnrichingCompanies,
      handleBulkRevealResponse,
      locationHook.search,
      locationHook.pathname,
      historyHook,
      buildCreatedDateRangeParam,
    ],
  );

  const handleRemoveFromList = useCallback(
    async (listIds: number[], onSuccess?: () => void) => {
      if (!listIds || listIds.length === 0) {
        toaster.error('Please select at least one list');
        return;
      }

      const isPeopleTab = activeLeadType === LeadFinderPrimaryTab.PEOPLE;
      const rowLeadId = leadIdToAddFromRowRef.current;
      const rowCompanyId = companyIdToAddFromRowRef.current;
      const isFromRowAction = rowLeadId != null || rowCompanyId != null;
      const isSelectedAllLeadsForAction =
        !isFromRowAction && isSelectedAllLeads;
      const isSelectedAllCompaniesForAction =
        !isFromRowAction && isSelectedAllCompanies;
      const isSelectedAll = isPeopleTab
        ? isSelectedAllLeadsForAction
        : isSelectedAllCompaniesForAction;

      const ctx: ListManagementContext = {
        dispatch,
        selectedLeads,
        selectedCompanies,
        isSelectedAllLeads: isSelectedAllLeadsForAction,
        isSelectedAllCompanies: isSelectedAllCompaniesForAction,
        peopleMaxLeadsLimit,
        companyMaxLeadsLimit,
        companiesData,
        activeSubTab,
        activeLeadType,
        isCompanyTab,
        apiParam,
        savedApiParam,
        companySavedApiParam,
        companyApiParam,
        savedFilters,
        leadCredits,
        handleGeneratePayload,
        handleCompanyGeneratePayload,
        refreshListsAndSavedData,
        refreshPeopleLists,
        refreshCompanyLists,
        setEnrichingCompanies,
        setIsCreateListLoading,
        setLeadsData,
        setCompaniesData,
        savedSearchFiltersToUseRef,
        refreshLeadsAndStartPollingRef,
        maybeEnableNotificationPollingRef,
        addFirebaseNotificationRef,
        companyIdToAddFromRowRef,
        getEffectiveSelectedLeads,
        getEffectiveSelectedCompanies,
        handleBulkRevealResponse,
        normalizeListIdsForPayload,
      };

      try {
        setLoadingRemoveFromList(true);
        const isSavedTab = activeSubTab === LeadFinderSecondaryTab.SAVED;
        const isTotalOrNetNew =
          activeSubTab === LeadFinderSecondaryTab.TOTAL ||
          activeSubTab === LeadFinderSecondaryTab.NEW;

        const tabValue = isPeopleTab
          ? LeadFinderListTab.People
          : LeadFinderListTab.Company;

        let lastApiResponse: any = null;

        if (isSavedTab && isSelectedAll) {
          lastApiResponse = await removeFromListSavedBulk(
            ctx,
            tabValue,
            listIds,
          );
        } else if (isTotalOrNetNew && isSelectedAll) {
          lastApiResponse = await removeFromListTotalNewBulk(
            ctx,
            tabValue,
            listIds,
          );
        } else {
          lastApiResponse = await removeFromListSingle(ctx, tabValue, listIds);
        }

        toaster.success(
          lastApiResponse?.message ||
            lastApiResponse?.successMessage ||
            `${
              isPeopleTab ? 'Leads' : 'Companies'
            } removed from list(s) successfully`,
          {
            theme: Theme.New,
          },
        );
        // Clear selection after successful remove
        if (isPeopleTab) {
          clearPeopleSelection(dispatch);
        } else {
          clearCompanySelection(dispatch);
        }

        // Refresh data based on active subTab
        if (activeSubTab === LeadFinderSecondaryTab.SAVED) {
          const currentSavedApiParam = isPeopleTab
            ? savedApiParam
            : companySavedApiParam;
          const payloadForSaved: any = {
            start: currentSavedApiParam?.start || 1,
            take: currentSavedApiParam?.take || 25,
          };
          if (savedFilters?.search) {
            payloadForSaved.search = savedFilters.search;
          }
          if (savedFilters?.tags && savedFilters.tags.length > 0) {
            payloadForSaved.tags = savedFilters.tags.join(',');
            payloadForSaved.tagIds = savedFilters.tags;
          }
          if (
            savedFilters?.createdDate &&
            savedFilters.createdDate.length === 2
          ) {
            const [startDate, endDate] = savedFilters.createdDate;
            payloadForSaved.createdDate = buildCreatedDateRangeParam(
              startDate,
              endDate,
              isPeopleTab,
            );
          }
          if (savedFilters?.listIds) {
            const normalized = normalizeListIdsForPayload(savedFilters.listIds);

            if (normalized) payloadForSaved.listIds = normalized;
          }
          if (!isPeopleTab && !payloadForSaved.listIds) {
            const fromUrl = getCompanyListIdsFromUrl();
            if (fromUrl) {
              payloadForSaved.listIds = fromUrl;
              payloadForSaved.companyList = fromUrl;
            }
          }
          if (savedFilters?.excludeProspects) {
            payloadForSaved.excludeProspects = savedFilters.excludeProspects;
          }
          if (
            savedFilters?.emailStatus &&
            savedFilters.emailStatus.length > 0
          ) {
            payloadForSaved.emailStatus = savedFilters.emailStatus;
          }

          if (isPeopleTab) {
            const res = await getSavedLeads(payloadForSaved);
            const normalizedData = normalizeSavedResponse(
              res,
              payloadForSaved,
              'profiles',
            );
            if (normalizedData) setLeadsData(normalizedData);
            const { start, take, ...countsPayload } = payloadForSaved;
            const decodedCreatedDate = decodeCreatedDateToRange(
              countsPayload?.createdDate,
            );
            if (decodedCreatedDate) {
              countsPayload.createdDate = decodedCreatedDate;
            }
            handleFetchSavedCounts?.(countsPayload);
            refreshPeopleLists();
          } else {
            const res = isPeopleTab
              ? await getSavedLeads(payloadForSaved)
              : await getSavedCompanies(payloadForSaved);
            const normalizedData = normalizeSavedResponse(
              res,
              payloadForSaved,
              'companies',
            );
            if (normalizedData) setCompaniesData(normalizedData);
            const { start, take, ...countsPayload } = payloadForSaved;
            const decodedCreatedDate = decodeCreatedDateToRange(
              countsPayload?.createdDate,
            );
            if (decodedCreatedDate) {
              if (
                Array.isArray(decodedCreatedDate) &&
                decodedCreatedDate.length === 2
              ) {
                const [startDate, endDate] = decodedCreatedDate;
                countsPayload.createdDate = `${startDate},${endDate}`;
              }
            }
            handleFetchSavedCounts?.(countsPayload);
            refreshCompanyLists();
          }
        }
        // For Total and Net New tabs, call searchLeads/searchCompanies with applied filters
        else if (isPeopleTab) {
          refreshLeadsAndStartPollingRef.current();
        } else {
          const urlFilters = new URLSearchParams(locationHook.search);
          if (handleCompanyTriggerSearchWithFilters) {
            handleCompanyTriggerSearchWithFilters(urlFilters);
          } else {
            const payload =
              typeof handleCompanyGeneratePayload === 'function'
                ? handleCompanyGeneratePayload(urlFilters)
                : undefined;
            searchCompanies(payload);
          }
        }

        // Also refresh both People and Company sidebar lists to keep sidebars in sync
        refreshPeopleLists();
        refreshCompanyListsForPeopleTab();
        refreshCompanyLists();

        // Call success callback to close modal
        onSuccess?.();
      } catch (error: any) {
        toaster.error(error?.message || 'Failed to remove leads from list(s)');
      } finally {
        setLoadingRemoveFromList(false);
        leadIdToAddFromRowRef.current = null;
        companyIdToAddFromRowRef.current = null;
      }
    },
    [
      activeLeadType,
      activeSubTab,
      selectedLeads,
      selectedCompanies,
      isSelectedAllLeads,
      isSelectedAllCompanies,
      handleGeneratePayload,
      handleCompanyGeneratePayload,
      savedApiParam,
      companySavedApiParam,
      apiParam,
      companyApiParam,
      searchLeads,
      searchCompanies,
      setLeadsData,
      setCompaniesData,
      savedFilters,
      dispatch,
      refreshLeadsAndStartPollingRef,
      refreshPeopleLists,
      refreshCompanyListsForPeopleTab,
      refreshCompanyLists,
      locationHook.search,
      locationHook.pathname,
      historyHook,
      isCompanyTab,
      leadIdToAddFromRowRef,
      companyIdToAddFromRowRef,
      buildCreatedDateRangeParam,
    ],
  );

  return {
    isCreateListLoading,
    isDeleteListLoading,
    loadingRemoveFromList,
    handleCreateListOnly,
    refreshListsAndSavedData,
    handleSaveList,
    handleDeleteList,
    handleExportList,
    handleAddToList,
    handleRemoveFromList,
  };
};
